import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { ResponseStatus } from 'src/@shared/shared/enum';
import { InnerPagesService } from '../../inner-pages.service';
import { UserProfileApiService } from './user-profile-api.service';

interface ApiEnvelope<T> {
  responseStatus: ResponseStatus;
  message: string;
  result: T | null;
}

export type ProfilePictureResponse = ApiEnvelope<{ profileUrl?: string | null }>;

/**
 * Current user's profile picture, shared by the header avatar menu and User details, so an
 * upload or removal on one shows up on the other at once. Backed by `User/ProfilePicture`
 * (POST form data: `File` to upload, `Remove=true` to delete) and the user details endpoint.
 */
@Injectable({
  providedIn: 'root',
})
export class ProfilePictureService {
  private readonly pictureSubject = new BehaviorSubject<string | null>(null);
  readonly picture$ = this.pictureSubject.asObservable();
  private isLoaded = false;

  constructor(private innerPages: InnerPagesService, private profileApi: UserProfileApiService) {}

  /** Fetches the picture once per session; later callers reuse the cached value. */
  load(): void {
    if (this.isLoaded) {
      return;
    }
    this.isLoaded = true;
    this.profileApi.getDetails().subscribe({
      next: (response) => {
        if (response.responseStatus === ResponseStatus.success) {
          this.pictureSubject.next(response.result?.profile || null);
        } else {
          this.isLoaded = false;
        }
      },
      error: () => (this.isLoaded = false),
    });
  }

  upload(file: File): Observable<ProfilePictureResponse> {
    const formData = new FormData();
    formData.set('File', file);
    return this.send(formData);
  }

  remove(): Observable<ProfilePictureResponse> {
    const formData = new FormData();
    formData.set('Remove', 'true');
    return this.send(formData);
  }

  /** For screens that change the picture through their own call (e.g. the legacy My Profile page). */
  set(url: string | null): void {
    this.isLoaded = true;
    this.pictureSubject.next(url);
  }

  private send(formData: FormData): Observable<ProfilePictureResponse> {
    return this.innerPages.manageProfilePicture(formData).pipe(
      tap((response: ProfilePictureResponse) => {
        if (response.responseStatus === ResponseStatus.success) {
          this.profileApi.clearDetailsCache();
          this.pictureSubject.next(response.result?.profileUrl || null);
        }
      })
    );
  }
}
